import type { ApiError, ApiResponse } from "@/lib/api";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL || "https://www.lilylanka.com";
console.log("🔧 Wishlist Service Initialized:", {
  API_BASE_URL,
  isDefined: !!API_BASE_URL,
  nodeEnv: process.env.NODE_ENV,
});
export interface WishlistResponse {
  items: string[]; // Array of hotel UIDs as strings
}

export interface AddToWishlistResponse {
  wishlistUid: number;
  hotelUid: number;
}

/**
 * Helper function to detect if response is HTML (error page)
 */
function isHtmlResponse(text: string): boolean {
  return text.trim().startsWith("<!DOCTYPE") || text.trim().startsWith("<html");
}

/**
 * Helper function to extract useful error message from response
 */
function getErrorMessage(responseText: string, status: number): string {
  if (isHtmlResponse(responseText)) {
    return `HTTP ${status}: API endpoint not found. Check if the API server is running and the endpoint URL is correct.`;
  }
  if (!responseText) {
    return `HTTP ${status}: No response body`;
  }
  try {
    const json = JSON.parse(responseText);
    return json.detail || json.message || JSON.stringify(json);
  } catch {
    return responseText.substring(0, 200); // Return first 200 chars of response
  }
}

/**
 * GET /api/Wishlist - Get user's wishlist
 */
export async function getWishlist(
  token: string,
): Promise<ApiResponse<WishlistResponse>> {
  try {
    const url = `${API_BASE_URL}/api/Wishlist`;
    console.log("🔍 Fetching wishlist:", {
      url,
      apiBaseUrl: API_BASE_URL,
      tokenExists: !!token,
    });

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    console.log("📦 Wishlist response:", {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      contentType: response.headers.get("content-type"),
    });

    if (response.ok) {
      try {
        const data = await response.json();
        console.log("✅ Wishlist data received:", data);
        return {
          success: true,
          data: data as WishlistResponse,
          status: response.status,
        };
      } catch (parseError) {
        console.error("❌ Failed to parse wishlist response:", parseError);
        return {
          success: false,
          data: {
            title: "Parse Error",
            detail: "Failed to parse wishlist data",
          },
          status: response.status,
        };
      }
    } else {
      const responseText = await response.text();
      const errorMessage = getErrorMessage(responseText, response.status);

      console.error("❌ Get wishlist API failed:", {
        status: response.status,
        statusText: response.statusText,
        url,
        isHtmlResponse: isHtmlResponse(responseText),
        errorMessage,
      });

      return {
        success: false,
        data: {
          title: response.statusText || "Failed to fetch wishlist",
          status: response.status,
          detail: errorMessage,
        },
        status: response.status,
      };
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorType =
      error instanceof TypeError ? "TypeError (Network/CORS)" : "Unknown Error";
    const errorStack = error instanceof Error ? error.stack : "No stack trace";

    console.error("❌ Get Wishlist network error:", {
      errorType,
      message: errorMessage,
      stack: errorStack,
      url: `${API_BASE_URL}/api/Wishlist`,
      apiBaseUrl: API_BASE_URL,
      apiBaseDefined: !!API_BASE_URL,
      fullError: JSON.stringify(error, null, 2),
    });

    console.error("❌ Raw error object:", error);

    return {
      success: false,
      data: {
        title: "Network Error",
        detail: `${errorType}: ${errorMessage}. Check API_BASE_URL configuration and ensure API server is running at ${API_BASE_URL}`,
      },
      status: 0,
    };
  }
}

/**
 * POST /api/Wishlist/hotels/{hotelUid} - Add hotel to wishlist
 */
export async function addToWishlist(
  hotelUid: number,
  token: string,
): Promise<ApiResponse<AddToWishlistResponse>> {
  try {
    const url = `${API_BASE_URL}/api/Wishlist/hotels/${hotelUid}`;
    console.log("🔍 Adding to wishlist:", {
      hotelUid,
      tokenExists: !!token,
      url,
      apiBaseUrl: API_BASE_URL,
    });

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    console.log("📦 Add to wishlist response:", {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      contentType: response.headers.get("content-type"),
      url,
    });

    if (response.ok) {
      try {
        const data = await response.json();
        console.log("✅ Added to wishlist successfully:", data);
        return {
          success: true,
          data: data as AddToWishlistResponse,
          status: response.status,
        };
      } catch (parseError) {
        console.error("❌ Failed to parse success response JSON:", parseError);
        return {
          success: true,
          data: {} as AddToWishlistResponse,
          status: response.status,
        };
      }
    } else {
      const responseText = await response.text();
      const errorMessage = getErrorMessage(responseText, response.status);

      console.error("❌ Add to wishlist API failed:", {
        status: response.status,
        statusText: response.statusText,
        url,
        contentType: response.headers.get("content-type"),
        isHtmlResponse: isHtmlResponse(responseText),
        errorMessage,
      });

      let errorData: ApiError = {
        title: response.statusText || "Failed to add to wishlist",
        status: response.status,
        detail: errorMessage,
      };

      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("❌ Add to Wishlist network error:", {
      message: error instanceof Error ? error.message : String(error),
      url: `${API_BASE_URL}/api/Wishlist/hotels/${hotelUid}`,
      apiBaseUrl: API_BASE_URL,
    });
    return {
      success: false,
      data: {
        title: "Network Error",
        detail: error instanceof Error ? error.message : "Unknown error",
      },
      status: 0,
    };
  }
}

/**
 * DELETE /api/Wishlist/hotels/{hotelUid} - Remove hotel from wishlist
 */
export async function removeFromWishlist(
  hotelUid: number,
  token: string,
): Promise<ApiResponse<void>> {
  try {
    const url = `${API_BASE_URL}/api/Wishlist/hotels/${hotelUid}`;
    console.log("🔍 Removing from wishlist:", {
      hotelUid,
      url,
      apiBaseUrl: API_BASE_URL,
      tokenExists: !!token,
    });

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    console.log("📦 Remove from wishlist response:", {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      url,
    });

    if (response.ok) {
      console.log("✅ Successfully removed from wishlist");
      return {
        success: true,
        status: response.status,
      };
    } else {
      const responseText = await response.text();
      const errorMessage = getErrorMessage(responseText, response.status);

      console.error("❌ Remove from wishlist API failed:", {
        status: response.status,
        statusText: response.statusText,
        url,
        isHtmlResponse: isHtmlResponse(responseText),
        errorMessage,
      });

      return {
        success: false,
        data: {
          title: response.statusText || "Failed to remove from wishlist",
          status: response.status,
          detail: errorMessage,
        },
        status: response.status,
      };
    }
  } catch (error) {
    console.error("❌ Remove from Wishlist network error:", {
      message: error instanceof Error ? error.message : String(error),
      url: `${API_BASE_URL}/api/Wishlist/hotels/${hotelUid}`,
      apiBaseUrl: API_BASE_URL,
    });
    return {
      success: false,
      data: {
        title: "Network Error",
        detail: error instanceof Error ? error.message : "Unknown error",
      },
      status: 0,
    };
  }
}

/**
 * Check if a hotel is in the wishlist
 */
export async function isHotelInWishlist(
  hotelUid: number,
  token: string,
): Promise<boolean> {
  try {
    const response = await getWishlist(token);
    if (response.success && response.data) {
      const wishlistData = response.data as WishlistResponse;
      return wishlistData.items.includes(hotelUid.toString());
    }
    return false;
  } catch (error) {
    console.error("Error checking wishlist status:", error);
    return false;
  }
}
